import { useEffect, useState } from 'react'
import { CheckBox, FormikError, NVButton, TextField } from '@nv/basic-components'
import { AlertBanner, PhoneInput } from '@nv/components'
import { useConfigurationContext, useRegistrationUserContext } from '@nv/contexts'
import { useFormik } from 'formik'
import VoterRegistrationService from 'libs/services/src/lib/voter/VoterRegistrationService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { AddressInput } from '../address/AddressInput'
import { MailingAddressInput } from '../address/MailingAddressInput'
import { UploadDocs } from './upload_docs/UploadDocs'
import styles from './RegistrationStep2.module.scss'

export const RegistrationStep2 = ({
  uploadDocs,
  driversLicense,
  driversLicenseReq,
  electionConfirmations = [],
  declarations = [],
  disclaimer,
  acceptedFilesLink = '',
  useNVAddress = false,
}: any) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState('')
  const [uploadDocsReq, setUploadDocsReq] = useState(uploadDocs)
  const { registrationUser, setRegistrationUser } = useRegistrationUserContext()
  const { knownIdNumberTextFieldConfig, mailingAddressRequiredVoteByMail, enableDifferentMailingAddress, registrationPhoneRequired } =
    useConfigurationContext()

  const validationSchema = Yup.object().shape({
    differentMailingAddress: Yup.boolean(),
    mailingAddressNotListed: Yup.boolean(),
    searchMailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
      is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
        (differentMailingAddress && !mailingAddressNotList) ||
        (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && !mailingAddressNotList),
      then: () =>
        Yup.object().shape({
          formattedAddress: Yup.string().required(t('form.validations.address')),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),
    mailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
      is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
        (differentMailingAddress && mailingAddressNotList) ||
        (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && mailingAddressNotList),
      then: () =>
        Yup.object().shape({
          streetAddress: Yup.string().required(t('form.validations.street')),
          unitNumberPOBox: Yup.string().notRequired(),
          city: Yup.string().required(t('form.validations.city')),
          province: Yup.string().required(t('form.validations.province')),
          postalCode: Yup.string().required(t('form.validations.postalCode')),
        }),
      otherwise: () => Yup.object().notRequired(),
    }),
    email: Yup.string().email(t('admin.users.invalidEmail')).required(t('form.validations.email')),
    confirmEmail: Yup.string()
      .oneOf([Yup.ref('email'), null], t('admin.users.emailsMustMatch'))
      .required(t('admin.users.confirmEmailRequired')),
    phone: registrationPhoneRequired ? Yup.string().required(t('form.validations.phone')) : Yup.string(),
    ...electionConfirmations.reduce((acc, curr) => {
      acc[curr.field] = Yup.boolean()
      return acc
    }, {}),
    electionConfirmations: Yup.boolean().when(
      electionConfirmations.map((curr) => curr.field),
      {
        is: (...args) => args.some(Boolean),
        otherwise: (schema) => schema.required(t('form.validations.oneConfirmationIsRequired')),
      },
    ),

    ...declarations.reduce((acc, curr) => {
      if (curr.isRequired)
        acc[curr.field] = Yup.boolean()
          .oneOf([true], t('admin.users.mustDeclareEligibility'))
          .required(t('admin.users.mustDeclareEligibility'))
      else acc[curr.field] = Yup.boolean()
      return acc
    }, {}),
    ...(uploadDocsReq ? { proofOfIdentity: Yup.mixed().required(t('registration.proofRequired')) } : {}),
    ...(driversLicenseReq ? { driversLicenseNumber: Yup.string().required(t('registration.driversLicenseRequired')) } : {}),
  })

  const serializeFile = (file) => {
    if (!file) return null

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        resolve({
          name: file.name,
          type: file.type,
          size: file.size,
          content: reader.result,
        })
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const formik = useFormik({
    initialValues: {
      ...registrationUser,

      searchMailingAddress: {
        streetAddress: registrationUser?.searchMailingAddress?.streetAddress || '',
        unitNumberPOBox: registrationUser?.searchMailingAddress?.unitNumberPOBox || '',
        city: registrationUser?.searchMailingAddress?.city || '',
        province: registrationUser?.searchMailingAddress?.province || '',
        postalCode: registrationUser?.searchMailingAddress?.postalCode || '',
        formattedAddress: registrationUser?.searchMailingAddress?.formattedAddress || '',
        value: registrationUser?.searchMailingAddress?.value || '',
      },
      mailingAddress: {
        streetAddress: registrationUser?.mailingAddress?.streetAddress || '',
        unitNumberPOBox: registrationUser?.mailingAddress?.unitNumber || '',
        city: registrationUser?.mailingAddress?.city || '',
        province: registrationUser?.mailingAddress?.province || '',
        postalCode: registrationUser?.mailingAddress?.postalCode || '',
      },
      email: registrationUser?.email || '',
      confirmEmail: registrationUser?.confirmEmail || '',
      phone: registrationUser?.phone || '',
      addressNotListed: registrationUser?.addressNotListed || false,
      mailingAddressNotListed: registrationUser?.mailingAddressNotListed || false,
      differentMailingAddress: registrationUser?.differentMailingAddress || false,
      proofOfIdentity: registrationUser?.proofOfIdentity || null,
      supportedDocument: registrationUser?.supportedDocument || null,
      driversLicenseNumber: registrationUser?.driversLicenseNumber || '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values)
    },
  })
  const onSubmit = async (values) => {
    setServerError('')

    const serializedFiles = await Promise.all([serializeFile(values.proofOfIdentity), serializeFile(values.supportedDocument)])

    const serializedValues = {
      ...values,
      photoIDs: serializedFiles.filter(Boolean),
    }
    const existingData = registrationUser || {}
    const updatedData = { ...existingData, ...serializedValues }
    const latestValues = {
      ...registrationUser,
      ...formik.values,
      photoIDs: serializedFiles.filter(Boolean),
    }
    setRegistrationUser(latestValues)

    const registrationData = latestValues

    if (registrationData) {
      const photoFiles = registrationData.photoIDs.map((photo) => {
        const byteCharacters = atob(photo.content.split(',')[1])
        const byteArrays = []
        for (let offset = 0; offset < byteCharacters.length; offset++) {
          byteArrays.push(byteCharacters.charCodeAt(offset))
        }
        const byteArray = new Uint8Array(byteArrays)
        const file = new File([byteArray], photo.name, { type: photo.type })
        return file
      })

      const formData = new FormData()
      formData.append('votingChannel', registrationData.votingChannel)

      formData.append('firstName', registrationData.firstName)
      formData.append('middleName', registrationData.middleName)
      formData.append('lastName', registrationData.lastName)
      if (registrationData.singleName) {
        formData.append('singleName', registrationData.singleName)
      }
      formData.append('dateOfBirth', registrationData.dateOfBirth)

      formData.append('email', registrationData.email)
      formData.append('phone', registrationData.phone)

      formData.append('knownIdNumber', registrationData.driversLicenseNumber)

      if (registrationData.addressNotListed) {
        formData.append('address[streetAddress]', registrationData.address.streetAddress)
        formData.append('address[city]', registrationData.address.city)
        formData.append('address[province]', registrationData.address.province)
        formData.append('address[postalCode]', registrationData.address.postalCode)
        formData.append('address[unitNumberPOBox]', registrationData.address.unitNumberPOBox)
      } else {
        formData.append('address[streetAddress]', registrationData.searchAddress.streetAddress)
        formData.append('address[city]', registrationData.searchAddress.city)
        formData.append('address[province]', registrationData.searchAddress.province)
        formData.append('address[postalCode]', registrationData.searchAddress.postalCode)
        formData.append('address[unitNumberPOBox]', registrationData.searchAddress.unitNumberPOBox)
        formData.append('address[formattedAddress]', registrationData.searchAddress.formattedAddress)
        if (useNVAddress) {
          formData.append('address[_id]', registrationData.searchAddress._id)
        }
      }
      if (registrationData.differentMailingAddress || mailingAddressRequiredVoteByMail) {
        if (registrationData.mailingAddressNotListed) {
          formData.append('mailingAddress[streetAddress]', registrationData.mailingAddress.streetAddress)
          formData.append('mailingAddress[city]', registrationData.mailingAddress.city)
          formData.append('mailingAddress[province]', registrationData.mailingAddress.province)
          formData.append('mailingAddress[postalCode]', registrationData.mailingAddress.postalCode)
          formData.append('mailingAddress[unitNumberPOBox]', registrationData.mailingAddress.unitNumberPOBox)
        } else {
          formData.append('mailingAddress[streetAddress]', registrationData.searchMailingAddress.streetAddress)
          formData.append('mailingAddress[city]', registrationData.searchMailingAddress.city)
          formData.append('mailingAddress[province]', registrationData.searchMailingAddress.province)
          formData.append('mailingAddress[postalCode]', registrationData.searchMailingAddress.postalCode)
          formData.append('mailingAddress[unitNumberPOBox]', registrationData.searchMailingAddress.unitNumberPOBox)
          formData.append('mailingAddress[formattedAddress]', registrationData.searchMailingAddress.formattedAddress)
        }
      }
      photoFiles.forEach((file) => {
        formData.append('photoIDs', file)
      })
      if (electionConfirmations) {
        let electionOptIns = ''
        for (var obj of electionConfirmations) {
          if (registrationData[obj.field]) {
            if (electionOptIns.length) {
              electionOptIns += ',' + obj.field
            } else {
              electionOptIns += obj.field
            }
          }
        }

        formData.append('electionOptIns', electionOptIns)
      }

      setRegistrationUser({ ...updatedData, formData })
      const eligible = await VoterRegistrationService.checkEligible(formData)
      if (eligible.error) {
        switch (eligible.error.errorCode) {
          case 'REG_ID_REQ':
            setUploadDocsReq(true)
            setServerError('REG_ID_REQ')
            break
          case 'REGISTERED':
            setServerError('REGISTERED')
            break
          case 'LEGAL_NAME_MISMATCH':
            setServerError('LEGAL_NAME_MISMATCH')
            break
          default:
            setServerError('INELIGIBLE')
            break
        }
      } else {
        navigate('/registration/step3')
      }
    }
  }

  useEffect(() => {
    if (formik.values.driversLicenseNumber) {
      setUploadDocsReq(false)
    } else {
      setUploadDocsReq(true)
    }
  }, [formik.values.driversLicenseNumber])

  return (
    <div className={styles['container']}>
      <div className={styles['title']}>{t('registration.voterRegistration')}</div>
      <form onSubmit={formik.handleSubmit} className={styles['form']}>
        {enableDifferentMailingAddress ? (
          <>
            <div className={styles['form__header']}>{t('registration.mailingAddress')}</div>
            <CheckBox
              formik={formik}
              field='differentMailingAddress'
              checked={formik.values.differentMailingAddress}
              onChange={() => formik.setFieldValue('differentMailingAddress', !formik.values.differentMailingAddress)}
              label={t('registration.differentMailingAddress')}
              ariaLabel={t('registration.differentMailingAddress')}
              square={true}
            />
            {formik.values.differentMailingAddress && (
              <AddressInput
                formik={formik}
                styles={styles}
                addressFieldName='mailingAddress'
                searchAddressFieldName='searchMailingAddress'
                addressNotListedFieldName='mailingAddressNotListed'
              />
            )}
          </>
        ) : formik.values.votingChannel === 'mail' && mailingAddressRequiredVoteByMail ? (
          <>
            <div className={styles['form__header']}>{t('registration.mailingAddress')}</div>
            <MailingAddressInput formik={formik} styles={styles} />
          </>
        ) : null}

        {/* Contact Information */}
        <div className={styles['form__header']}>{t('registration.contactInformation')}</div>
        <div className={styles['form__email-section']}>
          <TextField
            formik={formik}
            type='email'
            field='email'
            placeholder={t('form.labels.email')}
            label={t('form.labels.email')}
            isRequired={true}
          />
          <TextField
            formik={formik}
            type='email'
            field='confirmEmail'
            placeholder={t('form.labels.confirmEmail')}
            label={t('form.labels.confirmEmail')}
            isRequired={true}
          />
        </div>
        <div className={styles['form__phone-section']}>
          <PhoneInput formik={formik} field='phone' isRequired={registrationPhoneRequired} />
        </div>

        <div className={styles['form__header']}>{t('registration.proofOfIdentity')}</div>
        {driversLicense && (
          <div className={styles['form__license']}>
            <TextField
              formik={formik}
              type='text'
              field='driversLicenseNumber'
              placeholder='00000000'
              label={t('registration.driversLicenseNumber')}
              isRequired={driversLicenseReq}
              {...knownIdNumberTextFieldConfig}
            />
          </div>
        )}

        {uploadDocs && uploadDocsReq && (
          <>
            {serverError === 'REG_ID_REQ' && <AlertBanner text={t('registration.form.errors.uploadProofOfIdentity')} error={true} />}
            <UploadDocs
              formik={formik}
              setFieldValue={formik.setFieldValue}
              fileField1='proofOfIdentity'
              fileField2='supportedDocument'
              req={uploadDocsReq}
              acceptedFilesLink={acceptedFilesLink}
            />
          </>
        )}

        <div className={styles['hr']}></div>
        {electionConfirmations &&
          electionConfirmations.map((confirmation, i) => {
            return (
              <CheckBox
                key={i}
                formik={formik}
                field={confirmation.field}
                checked={formik.values[confirmation.field]}
                onChange={() => formik.setFieldValue(confirmation.field, !formik.values[confirmation.field])}
                label={confirmation.label}
                ariaLabel={confirmation.label}
                isRequired={confirmation.isRequired}
                square={true}
              />
            )
          })}
        <FormikError formik={formik} fieldName={'electionConfirmations'} />

        {declarations &&
          declarations.map((declaration, i) => {
            return (
              <CheckBox
                key={i}
                formik={formik}
                field={declaration.field}
                checked={formik.values[declaration.field]}
                onChange={() => formik.setFieldValue(declaration.field, !formik.values[declaration.field])}
                label={declaration.label}
                ariaLabel={declaration.label}
                isRequired={declaration.isRequired}
                square={true}
              />
            )
          })}

        {disclaimer}

        {(serverError === 'REGISTERED' || serverError === 'INELIGIBLE' || serverError === 'LEGAL_NAME_MISMATCH') && (
          <AlertBanner text={t(`registration.form.errors.${serverError.toLocaleLowerCase()}`)} error={true} />
        )}
        {/* Buttons */}
        <div className={styles['form__actions']}>
          <NVButton
            variant='primary'
            text={t('buttons.back')}
            height={50}
            onClick={() => {
              setRegistrationUser({ ...registrationUser, ...formik.values })
              navigate('/registration/step1')
            }}
          />
          <NVButton text={t('buttons.next')} height={50} onClick={formik.handleSubmit} />
        </div>
      </form>
    </div>
  )
}
